import { deriveCostRecipe, type CostRecipe, type DimensionPricing } from "./cost-recipe";
import type { QuantityDerivation } from "./quantity-dimensions";
import { resolveSectionSupply } from "./section-supply-pricing";
import { matchesDimensionLabel } from "./work-dimensions";
export { steelSectionsFromEvidence } from "./section-supply-pricing";
export { ensureLengthDimensions, markDimensionPriceReview } from "./length-dimensions";
export { isSteelMemberWork, workDimensionHeading } from "./work-dimensions";
export { matchesDimensionLabel } from "./work-dimensions";

const penny = (n: number) => Math.round(n * 100) / 100;
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
type MeasureLabels = Pick<DimensionPricing, "measure" | "dimension_labels">;
function instanceLengths(model: MeasureLabels, data?: QuantityDerivation | null, quantity?: number, unit?: string) {
  if (model.measure !== "length" || model.dimension_labels.length !== 1
    || !(data?.quantity_op === "count" || ["each", "item", "items", "no", "nr", "unit"].includes(unit?.toLowerCase().trim() ?? ""))) return null;
  const key = norm(model.dimension_labels[0]);
  const instances = (data?.dimensions ?? []).filter(d => matchesDimensionLabel(d.label, key));
  return instances.length > 1 && instances.length === quantity
    && new Set(instances.map(d => d.id)).size === instances.length && instances.every(d => d.unit === "m")
    ? instances : null;
}
function instanceReference<T extends MeasureLabels & { reference_quantity: number; reference_dimensions?: DimensionPricing["reference_dimensions"] }>(model: T,
  data?: QuantityDerivation | null, quantity?: number, unit?: string): T {
  const instances = instanceLengths(model, data, quantity, unit);
  if (!instances) return model;
  // A previously unresolved set retains its selected quote price at the original
  // measurements. Anchor variable work to that set's original mean; fixed costs
  // remain per installation. Original values survive repeated edits and saving.
  const reference = instances.reduce((n, d) => n + (d.originalValue ?? d.value), 0) / instances.length;
  return reference > 0 ? { ...model, reference_quantity: reference,
    // Length models have one reference axis. Keep its quote-local value in sync
    // with the original per-instance mean so the recipe remains self-consistent.
    ...(model.reference_dimensions ? { reference_dimensions: model.reference_dimensions.map(d => ({ ...d, value: reference })) } : {}),
  } : model;
}
export const hasDimensionPricing = (r: CostRecipe) =>
  !!r.labour_pricing || !!r.plant_pricing ||
  (r.shape === "specification" && !!r.supply_pricing) || r.materials.some(m => !!m.dimension_pricing || !!m.quantity_pricing);
export const measureUnit = (measure: DimensionPricing["measure"]) =>
  ({ length: "m", area: "m²", volume: "m³", weight: "kg", count: "each" })[measure];

/** Quote measurements have one source: Dimensions, never hidden material-form overrides.
 * Manual prices are deliberately left alone. Legacy snapshots are only updated
 * when the caller explicitly recalculates (measurement edit / Materials opened).
 */
export function priceRecipeFromDimensions(input: unknown, selectedRate: number, dimensions?: QuantityDerivation | null, sectionText?: string, workQuantity?: number, workUnit?: string): CostRecipe {
  const recipe = deriveCostRecipe(input).recipe;
  if (recipe.quote_pricing?.status === "manual") return recipe;
  const clean = (model: DimensionPricing): DimensionPricing => {
    const { actual_quantity, selected_section, resolved_quantity, resolved_section, resolved_spec, ...rest } = model;
    return rest;
  };
  const priced = priceRecipeForQuote({
    ...recipe,
    ...(recipe.labour_pricing ? { labour_pricing: clean(recipe.labour_pricing) } : {}),
    ...(recipe.plant_pricing ? { plant_pricing: clean(recipe.plant_pricing) } : {}),
    ...(recipe.shape === "specification" && recipe.supply_pricing ? { supply_pricing: clean(recipe.supply_pricing) } : {}),
    materials: recipe.materials.map(m => m.dimension_pricing ? { ...m, dimension_pricing: clean(m.dimension_pricing) } : m),
  }, selectedRate, dimensions, sectionText, workQuantity, workUnit);
  // Apply the quote's whole-pound rule AFTER every measurement calculation.
  // Keep supplier costs, measurement precision and recalculation anchors exact.
  const calculated = Number(deriveCostRecipe(priced).rateTypical);
  const whole = Math.floor(calculated);
  const pennies = penny(calculated - whole);
  if (pennies > 0) {
    if (penny(priced.labour_allowance + priced.plant_allowance) < pennies) {
      throw new RangeError("Cannot remove pennies without reducing material costs; review the installation allowance.");
    }
    const fromLabour = Math.min(priced.labour_allowance, pennies);
    priced.labour_allowance = penny(priced.labour_allowance - fromLabour);
    priced.plant_allowance = penny(priced.plant_allowance - penny(pennies - fromLabour));
    priced.quote_pricing?.notes.push(
      `Calculated rate £${calculated.toFixed(2)} reduced to £${whole.toFixed(2)} by removing pennies from installation; supplier costs unchanged.`,
    );
  }
  return deriveCostRecipe(priced).recipe;
}

/** No parsing measurements from prose, no evaluated formulas, no trade-specific thresholds. */
export function resolveMaterialMeasure(model: DimensionPricing, data?: QuantityDerivation | null, workQuantity?: number, workUnit?: string): number | null {
  const normalise = (actual: number): number | null => {
    if (!Number.isFinite(actual) || actual < 0) return null;
    if (model.quantity_basis !== "per_work_unit") return actual;
    const qty = workQuantity;
    // A zero-count line has no consumption, but retain its reusable unit basis.
    if (qty === 0 && actual === 0) return model.reference_quantity;
    return qty != null && Number.isFinite(qty) && qty > 0 ? actual / qty : null;
  };
  if (model.actual_quantity != null) return normalise(model.actual_quantity);
  const dims = data?.dimensions ?? [];
  // One labelled measurement for every counted instance is not ambiguous.
  // Linear variable work adds across instances; the unit price uses their mean
  // while the caller retains the count and each instance's fixed allowance.
  // Never extend this inference to area/volume-priced quantities or partial sets.
  const instances = instanceLengths(model, data, workQuantity, workUnit);
  if (instances) return instances.reduce((n, d) => n + d.value, 0) / instances.length;
  const found = model.dimension_labels.map(label => {
    const key = norm(label);
    const matches = dims.filter(d => matchesDimensionLabel(d.label, key));
    return matches.length === 1 ? matches[0] : null;
  });
  if (found.some(d => !d) || new Set(found.map(d => d?.id)).size !== found.length) return null;
  const ds = found.filter(d => d != null);
  if (model.measure === "weight" && ds.length === 2) {
    if (ds.filter(d => d.unit === "m").length !== 1 || ds.filter(d => d.unit === "kg/m").length !== 1) return null;
    return normalise(ds[0].value * ds[1].value);
  }
  const expectedCount = model.measure === "volume" ? 3 : model.measure === "area" ? 2 : 1;
  // Area/volume can also reference an explicit area/volume measurement.
  if (ds.length === 1 && ds[0].unit === measureUnit(model.measure)) return normalise(ds[0].value);
  if (ds.length !== expectedCount || ds.some(d => d.unit !== (["area", "volume"].includes(model.measure) ? "m" : measureUnit(model.measure))))
    return null;
  return normalise(ds.reduce((n, d) => n * d.value, 1));
}

export function priceMeasuredMaterial(model: DimensionPricing, baseline: number, actual: number) {
  const reference = model.reference_price ?? baseline;
  if (model.fixed_cost > reference) throw new RangeError("Fixed material cost cannot exceed its reference price");
  const price = penny(model.fixed_cost + (reference - model.fixed_cost) * actual / model.reference_quantity);
  if (!Number.isFinite(price) || price < 0 || price > 99_999_999.99) throw new RangeError("Calculated material price is out of range");
  return price;
}

/**
 * Library bands represent baseline materials + installation. Installation only
 * varies with measurements when its recipe explicitly declares that dependency.
 * Snapshots retain all anchors, so re-rendering/recalculating cannot compound costs.
 */
export function priceRecipeForQuote(input: unknown, selectedRate: number, dimensions?: QuantityDerivation | null, sectionText?: string, workQuantity?: number, workUnit?: string): CostRecipe {
  const original = deriveCostRecipe(input);
  const source = original.recipe;
  if (!Number.isFinite(selectedRate) || selectedRate < 0) throw new RangeError("Invalid selected library rate");
  const prior = source.quote_pricing;
  if (prior?.status === "manual") return source;
  // Use the recipe's aggregate rounding, not a sum of penny-rounded rows.
  // Otherwise attaching measurement pricing can move an unchanged base by 1p.
  const baselineMaterials = prior?.baseline_materials ?? penny(
    Number(original.rateTypical) - source.labour_allowance - source.plant_allowance);
  const labour = prior?.baseline_labour ?? source.labour_allowance;
  const plant = prior?.baseline_plant ?? source.plant_allowance;
  const selected = prior?.selected_rate ?? selectedRate;
  const residual = penny(selected - baselineMaterials);
  if (residual < 0) throw new RangeError("Selected rate is below baseline materials. Review the library breakdown; installation cannot be negative.");
  if (labour + plant === 0 && residual !== 0)
    throw new RangeError("This breakdown has no installation allowance to reconcile with the selected band.");
  const baseLabour = labour + plant > 0 ? penny(residual * labour / (labour + plant)) : 0;
  const basePlant = penny(residual - baseLabour);
  const notes: string[] = [
    `Selected base rate £${selected.toFixed(2)} includes £${baselineMaterials.toFixed(2)} reference materials and £${residual.toFixed(2)} installation. Only explicitly configured measurement dependencies vary; other allowances remain fixed.`,
  ];
  let missing = false;
  const apply = (model: DimensionPricing, price: number, name: string) => {
    model = instanceReference(model, dimensions, workQuantity, workUnit);
    if (model.section_rates) {
      const reference = model.reference_price ?? price;
      const { resolved_quantity: _q, resolved_spec: _s, resolved_section: _r, ...clean } = model;
      const result = resolveSectionSupply({ ...clean, reference_price: reference }, dimensions, sectionText);
      if ("error" in result) {
        missing = true;
        notes.push(`${name}: ${result.error}`);
        return { price: reference, model: { ...clean, reference_price: reference } };
      }
      notes.push(`${name}: ${result.working} → £${result.price.toFixed(2)}`);
      return { price: result.price, model: { ...clean, reference_price: reference,
        resolved_quantity: result.quantity, resolved_section: result.section || undefined, resolved_spec: result.working } };
    }
    const actual = resolveMaterialMeasure(model, dimensions, workQuantity, workUnit);
    const reference = model.reference_price ?? price;
    if (actual == null) {
      missing = true;
      notes.push(`${name}: enter ${model.dimension_labels.join(" × ")} (${measureUnit(model.measure)}). Reference allowance retained; not dimension-priced.`);
      const { resolved_quantity: _q, resolved_spec: _s, ...clean } = model;
      return { price: reference, model: { ...clean, reference_price: reference } };
    }
    const next = priceMeasuredMaterial(model, reference, actual);
    const instances = instanceLengths(model, dimensions, workQuantity, workUnit);
    const spec = model.actual_quantity != null
      ? `${actual} ${measureUnit(model.measure)} (entered for this quote)`
      : instances ? instances.map(d => `${d.label}: ${d.value} ${d.unit}`).join("; ")
      : model.dimension_labels.map(label => {
          const match = dimensions?.dimensions.find(d => matchesDimensionLabel(d.label, label));
          return `${label}: ${match?.value} ${match?.unit}${match?.source === "assumed" ? " (assumed)" : ""}`;
        }).join(" × ");
    notes.push(`${name}: ${actual.toFixed(4)} ${measureUnit(model.measure)}; reference ${model.reference_quantity} ${measureUnit(model.measure)} at £${reference.toFixed(2)}; fixed £${model.fixed_cost.toFixed(2)} → £${next.toFixed(2)}. ${model.evidence}`);
    return { price: next, model: { ...model, reference_price: reference, resolved_quantity: Math.round(actual * 10000) / 10000, resolved_spec: spec } };
  };
  const recipe: CostRecipe = {
    ...source, labour_allowance: baseLabour, plant_allowance: basePlant,
    materials: source.materials.map(m => {
      if (m.quantity_pricing) {
        const model = instanceReference(m.quantity_pricing, dimensions, workQuantity, workUnit);
        const reference = model.reference_factor ?? m.base_factor;
        const actual = resolveMaterialMeasure({ ...model, fixed_cost: 0 }, dimensions, workQuantity, workUnit);
        if (actual == null) {
          missing = true;
          notes.push(`${m.name}: enter ${model.dimension_labels.join(" × ")}; reference consumption retained.`);
        }
        const factor = actual == null ? reference : Math.round(
          (model.fixed_quantity + (reference - model.fixed_quantity) * actual / model.reference_quantity) * 10000) / 10000;
        if (!Number.isFinite(factor) || factor < 0 || factor > 99_999_999.99)
          throw new RangeError("Calculated material quantity is out of range");
        notes.push(`${m.name}: ${factor} ${m.unit} before ${m.waste_pct}% waste; merchant unit price unchanged. ${model.evidence}`);
        return { ...m, base_factor: factor, quantity_pricing: {
          ...model, reference_factor: reference, resolved_quantity: actual == null ? undefined : Math.round(actual * 10000) / 10000,
        } };
      }
      if (!m.dimension_pricing) return m;
      const adjusted = apply(m.dimension_pricing, m.unit_price, m.name);
      return { ...m, unit_price: adjusted.price, dimension_pricing: adjusted.model };
    }),
  };
  for (const key of ["labour", "plant"] as const) {
    const model = source[`${key}_pricing`];
    if (!model) continue;
    const canonical = key === "labour" ? labour : plant;
    const allowance = key === "labour" ? baseLabour : basePlant;
    // Preserve the selected band's installation residual, including its fixed
    // portion. Canonical reference anchors remain unchanged in the snapshot.
    const result = apply({ ...model, reference_price: model.reference_price ?? canonical },
      canonical, key === "labour" ? "Labour" : "Plant");
    recipe[`${key}_allowance`] = canonical > 0 ? penny(allowance * result.price / canonical) : allowance;
    recipe[`${key}_pricing`] = result.model;
  }
  if (recipe.shape === "specification" && recipe.supply_pricing) {
    const adjusted = apply(recipe.supply_pricing, recipe.supply_allowance, recipe.supply_name ?? "Supply");
    recipe.supply_allowance = adjusted.price;
    recipe.supply_pricing = adjusted.model;
  }
  recipe.quote_pricing = {
    selected_rate: selected, baseline_materials: baselineMaterials, baseline_labour: labour, baseline_plant: plant,
    status: missing ? "needs_measurements" : "calculated", notes,
  };
  return deriveCostRecipe(recipe).recipe;
}

/** Explicit manual prices are authoritative, not silently undone on the next render. */
export function manualRecipePrice(recipe: CostRecipe): CostRecipe {
  return recipe.quote_pricing ? { ...recipe, quote_pricing: { ...recipe.quote_pricing, status: "manual",
    notes: ["Manually edited quote breakdown. Automatic measurement pricing paused for this item."] } } : recipe;
}
